/**
 * visual-review: before/after pairs for captured media (screenshots, level videos). Each capture is
 * pinned to the commit it was taken at; a "before" is the capture of the same name at the base
 * commit. Pairs render side by side or as a slider, with the sidecar stats and the full provenance
 * one tap away, and a stale-baseline warning when the "before" is not from the base the change was
 * built on (the failure that made the user ask "is the before really master?").
 */
import { useQuery } from '@tanstack/react-query'
import {
  type ArtifactProvenance,
  mediaKindOf,
  type PairableArtifact,
  pairBeforeAfter,
  type StaleBaseline,
} from '@viko/protocol'
import { TriangleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { ArtifactViewerAccess } from '../lib/artifact-viewer.js'
import { useBackend } from '../lib/backend.js'
import type { Artifact } from '../lib/types.js'
import { useOpenedArtifact } from './EncryptedArtifact.js'
import '../routes/project-views.css'
import './before-after.css'
import { type CompareMode, CompareModeSwitch, MediaSlider, MediaSplit } from './media-compare.js'

type Tagged = PairableArtifact & { a: Artifact }

/** Split a project's artifacts into before/after pairs and the rest (kept in the gallery). */
export function splitPaired(list: readonly Artifact[]): {
  pairs: { key: string; before?: Artifact; after: Artifact; stale?: StaleBaseline }[]
  rest: Artifact[]
} {
  const tagged: Tagged[] = list.map((a) => ({
    id: a.id,
    name: a.name ?? a.id,
    createdAt: a.createdAt,
    ...(a.provenance ? { provenance: a.provenance } : {}),
    a,
  }))
  const { pairs } = pairBeforeAfter(tagged)
  const inPair = new Set<string>()
  // An "after" with no baseline still gets the pair view (it says what is missing); a lone
  // "before" waits in the gallery until its "after" exists.
  for (const p of pairs) {
    inPair.add(p.after.id)
    if (p.before) inPair.add(p.before.id)
  }
  return {
    pairs: pairs.map((p) => ({
      key: p.key,
      after: (p.after as Tagged).a,
      ...(p.before ? { before: (p.before as Tagged).a } : {}),
      ...(p.stale ? { stale: p.stale } : {}),
    })),
    rest: list.filter((a) => !inPair.has(a.id)),
  }
}

type Source = { url?: string; mime: string; name: string; state: 'loading' | 'ok' | 'error' }

/** The decrypted (or signed-URL) media of one artifact. */
function useMedia(a: Artifact, access: ArtifactViewerAccess): Source {
  const b = useBackend()
  const encrypted = !!a.encrypted
  const ref =
    a.encrypted && a.encrypted.accountId === access.accountId && a.encrypted.objectId === a.id
      ? a.encrypted
      : undefined
  const opened = useOpenedArtifact(
    (access.reader ?? { open: async () => Promise.reject(new Error('locked')) }) as never,
    access.reader ? ref : undefined,
  )
  const signed = useQuery({
    queryKey: ['artifact-url', a.id],
    queryFn: () => b.api.artifactUrl(a.id),
    enabled: !encrypted && a.uploaded,
    staleTime: 4 * 60_000,
    retry: false,
  })
  const name = a.title ?? a.name ?? a.id
  const mime = (opened?.handle?.preview ?? a.mime ?? '').toLowerCase()
  if (encrypted) {
    if (!access.reader || !ref) return { mime, name, state: 'error' }
    if (opened?.failed) return { mime, name, state: 'error' }
    return opened?.handle
      ? { url: opened.handle.url, mime, name, state: 'ok' }
      : { mime, name, state: 'loading' }
  }
  if (signed.isError) return { mime, name, state: 'error' }
  return signed.data?.url
    ? { url: signed.data.url, mime, name, state: 'ok' }
    : { mime, name, state: 'loading' }
}

function MediaView({ src }: { src: Source }) {
  if (src.state === 'error') return <p role="alert">Locked or unavailable</p>
  if (!src.url) return <p role="status">Loading…</p>
  if (src.mime.startsWith('video/') || mediaKindOf(src.name) === 'video')
    return (
      // biome-ignore lint/a11y/useMediaCaption: captured gameplay has no caption track
      <video src={src.url} controls preload="metadata" aria-label={src.name} />
    )
  return <img src={src.url} alt={src.name} />
}

const short = (s: string) => s.slice(0, 9)
const sha7 = (s: string) => s.slice(0, 7)

/** Sidecar stats as one inline row with deltas ("moves 13 → 11 · solver.ms 610 → 420 −31%"). */
export function statRow(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown> | undefined,
): { key: string; before?: string; after: string; delta?: string; better?: boolean }[] {
  const keys = [...new Set([...Object.keys(after ?? {}), ...Object.keys(before ?? {})])]
  return keys.map((key) => {
    const a = after?.[key]
    const b = before?.[key]
    const row: { key: string; before?: string; after: string; delta?: string; better?: boolean } = {
      key,
      after: a === undefined ? '—' : String(a),
    }
    if (b !== undefined && String(b) !== row.after) row.before = String(b)
    if (typeof a === 'number' && typeof b === 'number' && b !== 0 && a !== b) {
      const pct = Math.round(((a - b) / Math.abs(b)) * 100)
      if (Math.abs(pct) >= 1) {
        row.delta = `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`
        // Lower is better for times and counts of effort; higher for rates.
        row.better = /fps|score|stars|rate/i.test(key) ? a > b : a < b
      }
    }
    return row
  })
}

function Stats({ before, after, name }: { before?: Artifact; after: Artifact; name: string }) {
  const rows = statRow(before?.sidecar?.stats, after.sidecar?.stats)
  if (!rows.length) return null
  return (
    <dl className="ba__stats" aria-label={`Stats for ${name}`}>
      {rows.map((r) => (
        <div key={r.key}>
          <dt>{r.key}</dt>
          <dd>
            {r.before !== undefined ? (
              <>
                {r.before} <span aria-label="to">→</span>{' '}
              </>
            ) : null}
            {r.after}
            {r.delta ? (
              <span className={r.better ? 'ba__delta is-better' : 'ba__delta'}> {r.delta}</span>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function ProvenanceRows({ p, side }: { p: ArtifactProvenance | undefined; side: string }) {
  if (!p) return <p className="ba__prov-none">{side}: no recorded commit.</p>
  const rows: [string, string][] = [
    ['Commit', p.commit],
    ...(p.tree ? ([['Tree', p.tree]] as [string, string][]) : []),
    ...(p.branch ? ([['Branch', p.branch]] as [string, string][]) : []),
    ...(p.baseCommit ? ([['Base commit', p.baseCommit]] as [string, string][]) : []),
    ...(p.runId ? ([['Run', p.runId]] as [string, string][]) : []),
    ...(p.taskId ? ([['Task', p.taskId]] as [string, string][]) : []),
    ['Captured', new Date(p.capturedAt).toLocaleString()],
    ...(p.glob ? ([['Matched glob', p.glob]] as [string, string][]) : []),
    ...(p.dirty ? ([['Worktree', 'had uncommitted changes']] as [string, string][]) : []),
  ]
  return (
    <div className="ba__prov-side">
      <h5>{side}</h5>
      <dl>
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/** One muted provenance line; a click opens the full hashes for both sides. */
function Provenance({ before, after }: { before?: Artifact; after: Artifact }) {
  const b = before?.provenance
  const a = after.provenance
  const ago = (ts: number) => {
    const m = Math.max(1, Math.round((Date.now() - ts) / 60_000))
    return m < 60
      ? `${m}m ago`
      : m < 48 * 60
        ? `${Math.round(m / 60)}h ago`
        : `${Math.round(m / 1440)}d ago`
  }
  return (
    <details className="ba__prov">
      <summary>
        {b ? (
          <>
            Before <code>{sha7(b.commit)}</code>
            {b.branch ? ` on ${b.branch}` : ''}
            {' · '}
          </>
        ) : null}
        {a ? (
          <>
            {b ? 'after' : 'After'} <code>{sha7(a.commit)}</code>
            {a.branch ? ` on ${a.branch}` : ''}
            {` · captured ${ago(a.capturedAt)}`}
          </>
        ) : (
          'After: no recorded commit'
        )}
      </summary>
      <div className="ba__prov-body">
        {before ? <ProvenanceRows p={b} side="Before" /> : null}
        <ProvenanceRows p={a} side="After" />
      </div>
    </details>
  )
}

function Pair({
  pair,
  access,
}: {
  pair: ReturnType<typeof splitPaired>['pairs'][number]
  access: ArtifactViewerAccess
}) {
  const after = useMedia(pair.after, access)
  const before = useMedia(pair.before ?? pair.after, access)
  const [picked, setMode] = useState<CompareMode | undefined>()
  const still = (s: Source) => !s.mime.startsWith('video/') && mediaKindOf(s.name) !== 'video'
  const sliderOk = !!pair.before && still(before) && still(after)
  // Slider first for stills (app-clarity `beforeafter`); side by side is one click (or `s`).
  const mode: CompareMode = picked ?? (sliderOk ? 'slider' : 'side')
  const beforeLabel = pair.before?.provenance
    ? `Before · base ${short(pair.before.provenance.commit)}`
    : 'Before'
  const afterLabel = pair.after.provenance
    ? `After · ${short(pair.after.provenance.commit)}`
    : 'After'
  const caption = pair.after.title?.replace(/\s+after$/i, '')
  return (
    <section className="ba" aria-label={`Before and after: ${pair.key}`} data-pair={pair.key}>
      <header className="ba__head">
        <h4 className="ba__title">
          {caption ? <span>{caption}</span> : null}
          <code>{pair.key}</code>
        </h4>
        {pair.before ? (
          <CompareModeSwitch
            mode={mode}
            onChange={setMode}
            sliderOk={sliderOk}
            label={`Compare ${pair.key}`}
          />
        ) : null}
      </header>
      <Provenance {...(pair.before ? { before: pair.before } : {})} after={pair.after} />
      {pair.stale ? (
        <p role="alert" className="ba__stale" data-stale={pair.stale.reason}>
          <TriangleAlert size={16} aria-hidden="true" />
          <span>
            <strong>Stale baseline.</strong> {pair.stale.message} Capture it again at the base
            commit before trusting this comparison.
          </span>
        </p>
      ) : null}
      {!pair.before ? (
        <p className="ba__note" role="status">
          No capture of this name at the base commit yet, so there is nothing to compare against.
        </p>
      ) : null}
      {pair.before ? (
        mode === 'slider' && sliderOk ? (
          <MediaSlider
            before={<MediaView src={before} />}
            after={<MediaView src={after} />}
            beforeLabel={beforeLabel}
            afterLabel={afterLabel}
          />
        ) : (
          <MediaSplit
            left={<MediaView src={before} />}
            right={<MediaView src={after} />}
            leftLabel={beforeLabel}
            rightLabel={afterLabel}
          />
        )
      ) : (
        <div className="mc-media">
          <MediaView src={after} />
        </div>
      )}
      <Stats
        {...(pair.before ? { before: pair.before } : {})}
        after={pair.after}
        name={pair.after.name ?? pair.after.id}
      />
    </section>
  )
}

export function BeforeAfterSection({
  pairs,
  access,
}: {
  pairs: ReturnType<typeof splitPaired>['pairs']
  access: ArtifactViewerAccess
}) {
  const ordered = useMemo(() => pairs, [pairs])
  if (!ordered.length) return null
  return (
    <section aria-label="Before and after" className="pv-sec ba-list">
      <h3 className="pv-h">
        Before and after <span className="pv-h__count">{ordered.length}</span>
      </h3>
      {ordered.map((p) => (
        <Pair key={p.after.id} pair={p} access={access} />
      ))}
    </section>
  )
}
